import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Eye,
  Edit3,
  FileText,
  Layers,
} from 'lucide-react';

// Episode template variables
const EPISODE_VARIABLES: Record<string, { label: string; description: string }> = {
  episode_title: { label: 'Episode Title', description: 'The podcast episode title' },
  episode_description: { label: 'Episode Description', description: 'The podcast episode description' },
  episode_audio_url: { label: 'Audio URL', description: 'Direct link to episode audio file' },
  episode_artwork: { label: 'Artwork', description: 'Episode cover art image URL' },
  episode_duration: { label: 'Duration', description: 'Formatted episode duration (e.g., 25:30)' },
  podbean_episode_id: { label: 'Podbean ID', description: 'Episode ID for Podbean player embed (deprecated)' },
  clips_section: { label: 'Clips Section', description: 'Where clip content will be inserted' },
};

// Clip template variables
const CLIP_VARIABLES: Record<string, { label: string; description: string }> = {
  clip_number: { label: 'Clip Number', description: 'Sequential number (1, 2, 3...)' },
  clip_title: { label: 'Clip Title', description: 'The article/clip title' },
  clip_description: { label: 'Clip Description', description: 'Truncated article description (200 chars)' },
  clip_url: { label: 'Clip URL', description: 'Link to the original article' },
  clip_image: { label: 'Clip Image', description: 'Article featured image URL' },
};

// Preview data types
export interface ClipPreviewData {
  clip_title: string;
  clip_description: string;
  clip_url: string;
  clip_image: string;
}

export interface TemplatePreviewData {
  hasData: boolean;
  episode_title: string;
  episode_description: string;
  episode_audio_url: string;
  episode_artwork: string;
  episode_duration: string;
  podbean_episode_id: string;
  clips: ClipPreviewData[];
}

// Legacy interface for backward compatibility
export interface LegacyPreviewData {
  hasData: boolean;
  episode_title: string;
  episode_description: string;
  podbean_embed: string;
  clip_summary: string;
}

interface TemplateEditorProps {
  episodeTemplate: string;
  clipTemplate: string;
  onEpisodeTemplateChange: (html: string) => void;
  onClipTemplateChange: (html: string) => void;
  previewData?: TemplatePreviewData | null;
}

// Variable insertion buttons
interface VariableButtonsProps {
  variables: Record<string, { label: string; description: string }>;
  onInsert: (variable: string) => void;
}

const VariableButtons: React.FC<VariableButtonsProps> = ({ variables, onInsert }) => (
  <div className="flex flex-wrap gap-1 mb-2">
    {Object.entries(variables).map(([key, { label, description }]) => (
      <Button
        key={key}
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={() => onInsert(key)}
        title={description}
      >
        {label}
      </Button>
    ))}
  </div>
);

// Simple HTML textarea editor
interface HtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  variables: Record<string, { label: string; description: string }>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({
  value,
  onChange,
  placeholder,
  variables,
  textareaRef,
}) => {
  const insertVariable = (variableId: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = `{{${variableId}}}`;
    const newValue = value.substring(0, start) + text + value.substring(end);
    onChange(newValue);

    // Restore focus and cursor position after the inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  return (
    <div className="space-y-2">
      <VariableButtons variables={variables} onInsert={insertVariable} />
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full font-mono text-sm min-h-[200px] resize-y p-3 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        spellCheck={false}
      />
    </div>
  );
};

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  episodeTemplate,
  clipTemplate,
  onEpisodeTemplateChange,
  onClipTemplateChange,
  previewData,
}) => {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const episodeTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const clipTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Generate preview HTML
  const previewHtml = useMemo(() => {
    if (!episodeTemplate && !clipTemplate) {
      return '<p style="color: #666;">Enter a template to see the preview</p>';
    }

    // Use preview data or fallback to sample data
    const data = previewData?.hasData
      ? previewData
      : {
          episode_title: 'Sample Episode Title',
          episode_description: 'This is a sample episode description that would come from your podcast.',
          episode_audio_url: 'https://example.com/audio.mp3',
          episode_artwork: 'https://via.placeholder.com/300x300?text=Artwork',
          episode_duration: '25:30',
          podbean_episode_id: 'sample-id',
          clips: [
            {
              clip_title: 'Sample Article Title',
              clip_description: 'This is a sample article description...',
              clip_url: 'https://example.com/article',
              clip_image: 'https://via.placeholder.com/300x200?text=Article',
            },
            {
              clip_title: 'Another Article',
              clip_description: 'Another sample article description...',
              clip_url: 'https://example.com/article2',
              clip_image: '',
            },
          ],
        };

    // Generate clips HTML
    let clipsHtml = '';
    if (clipTemplate && data.clips) {
      clipsHtml = data.clips
        .map((clip, index) => {
          let html = clipTemplate;
          html = html.replace(/\{\{clip_number\}\}/g, String(index + 1));
          html = html.replace(/\{\{clip_title\}\}/g, clip.clip_title);
          html = html.replace(/\{\{clip_description\}\}/g, clip.clip_description);
          html = html.replace(/\{\{clip_url\}\}/g, clip.clip_url);
          html = html.replace(/\{\{clip_image\}\}/g, clip.clip_image);
          return html;
        })
        .join('\n');
    }

    // Generate episode HTML
    let html = episodeTemplate || '';
    html = html.replace(/\{\{episode_title\}\}/g, data.episode_title);
    html = html.replace(/\{\{episode_description\}\}/g, data.episode_description);
    html = html.replace(/\{\{episode_audio_url\}\}/g, data.episode_audio_url);
    html = html.replace(/\{\{episode_artwork\}\}/g, data.episode_artwork);
    html = html.replace(/\{\{episode_duration\}\}/g, data.episode_duration);
    html = html.replace(/\{\{podbean_episode_id\}\}/g, data.podbean_episode_id);
    html = html.replace(/\{\{clips_section\}\}/g, clipsHtml);

    return html || '<p style="color: #666;">Template is empty</p>';
  }, [episodeTemplate, clipTemplate, previewData]);

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit" className="flex items-center gap-2">
            <Edit3 className="h-4 w-4" />
            Edit Templates
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="space-y-6 mt-4">
          {/* Episode Template Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Episode Template
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Overall structure for each episode in the RSS feed. Use <code className="bg-muted px-1 rounded">{'{{clips_section}}'}</code> to insert all clips.
              </p>
            </CardHeader>
            <CardContent>
              <HtmlEditor
                value={episodeTemplate}
                onChange={onEpisodeTemplateChange}
                placeholder="Enter HTML template for episodes..."
                variables={EPISODE_VARIABLES}
                textareaRef={episodeTextareaRef}
              />
            </CardContent>
          </Card>

          {/* Clip Template Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Clip Template
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                This template repeats for each clip in the episode.
              </p>
            </CardHeader>
            <CardContent>
              <HtmlEditor
                value={clipTemplate}
                onChange={onClipTemplateChange}
                placeholder="Enter HTML template for clips..."
                variables={CLIP_VARIABLES}
                textareaRef={clipTextareaRef}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Live Preview
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {previewData?.hasData
                  ? 'Showing content from your most recent published episode'
                  : 'No published episodes - showing sample data'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preview content */}
              <div
                className="border rounded-md p-4 bg-white text-gray-900"
                style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  lineHeight: '1.6',
                }}
              >
                <style>{`
                  .preview-content h1 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem; color: #111; }
                  .preview-content h2 { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #222; }
                  .preview-content h3 { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.25rem; color: #333; }
                  .preview-content p { margin-bottom: 0.75rem; color: #444; }
                  .preview-content a { color: #2563eb; text-decoration: underline; }
                  .preview-content iframe { margin: 1rem 0; border-radius: 8px; }
                  .preview-content table { width: 100%; }
                  .preview-content audio { width: 100%; }
                `}</style>
                <div
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>

              {/* Data debug info */}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Available data ({previewData?.clips?.length || 0} clips)
                </summary>
                <div className="mt-2 p-3 bg-muted rounded-md space-y-1 font-mono">
                  <div><span className="text-muted-foreground">episode_title:</span> {previewData?.episode_title || '(empty)'}</div>
                  <div><span className="text-muted-foreground">episode_description:</span> {previewData?.episode_description ? `${previewData.episode_description.substring(0, 50)}...` : '(empty)'}</div>
                  <div><span className="text-muted-foreground">episode_audio_url:</span> {previewData?.episode_audio_url ? `${previewData.episode_audio_url.substring(0, 50)}...` : '(empty)'}</div>
                  <div><span className="text-muted-foreground">episode_artwork:</span> {previewData?.episode_artwork ? `${previewData.episode_artwork.substring(0, 50)}...` : '(empty)'}</div>
                  <div><span className="text-muted-foreground">episode_duration:</span> {previewData?.episode_duration || '(empty)'}</div>
                  <div><span className="text-muted-foreground">podbean_episode_id:</span> {previewData?.podbean_episode_id || '(empty)'}</div>
                  <div><span className="text-muted-foreground">clips:</span> {previewData?.clips?.length || 0} items</div>
                  {previewData?.clips?.map((clip, i) => (
                    <div key={i} className="pl-4 border-l-2 border-muted-foreground/30">
                      <div>clip[{i}].title: {clip.clip_title || '(empty)'}</div>
                      <div>clip[{i}].url: {clip.clip_url || '(empty)'}</div>
                    </div>
                  ))}
                </div>
              </details>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TemplateEditor;
